import { Box, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { ReactNode, ReactElement, Children, isValidElement, cloneElement } from 'react';
import { marketing } from '../styles/theme';

/**
 * Simple frontmatter parser (browser-compatible, no Buffer needed).
 * Shared across blog, research, and case-studies loaders.
 *
 * Tolerates malformed closing delimiters like `---# Heading` (no newline
 * between the closing `---` and the next content line). DTAC v1 section A/B
 * were authored that way; rather than fail silently we normalize by inserting
 * the missing newline before matching. Well-formed input is unchanged.
 */
export function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  const normalized = raw.replace(/^(---\s*\n[\s\S]*?\n---)(\S)/, '$1\n$2');
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = normalized.match(frontmatterRegex);

  if (!match) {
    return { data: {}, content: raw };
  }

  const [, frontmatterStr, content] = match;
  const data: Record<string, unknown> = {};

  // Parse simple YAML frontmatter
  let currentKey = '';
  let inArray = false;
  const arrayValues: string[] = [];

  for (const line of frontmatterStr.split('\n')) {
    const trimmed = line.trim();

    // Array item
    if (trimmed.startsWith('- ') && inArray) {
      const raw = trimmed.slice(2).trim();
      arrayValues.push(raw.replace(/^["']|["']$/g, ''));
      continue;
    }

    // If we were collecting array values, save them
    if (inArray && currentKey) {
      data[currentKey] = arrayValues.slice();
      arrayValues.length = 0;
      inArray = false;
    }

    // Key-value pair
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim();

      if (value === '') {
        // Could be start of array or multi-line
        currentKey = key;
        inArray = true;
      } else {
        // Remove quotes if present
        const unquoted = value.replace(/^["']|["']$/g, '');
        data[key] = unquoted;
      }
    }
  }

  // Handle final array if still collecting
  if (inArray && currentKey && arrayValues.length > 0) {
    data[currentKey] = arrayValues;
  }

  return { data, content: content.trim() };
}

/**
 * Factory for content-type-specific image URL resolvers.
 * All three content types (blog, research, case-studies) use identical resolution logic —
 * only the glob result differs.
 */
export function createImageResolver(images: Record<string, string>) {
  return function resolveImageUrl(imagePath: string | undefined): string | undefined {
    if (!imagePath) return undefined;
    if (imagePath.startsWith('http') || imagePath.startsWith('/images')) return imagePath;
    const filename = imagePath.split('/').pop();
    if (!filename) return undefined;
    for (const [path, url] of Object.entries(images)) {
      if (path.endsWith(`/${filename}`)) return url;
    }
    return undefined;
  };
}

// --- Callout support ---

const CALLOUT_CONFIG: Record<string, { icon: ReactElement; color: string; iconColor: string; bgColor: string }> = {
  QUOTE: { icon: <FormatQuoteIcon fontSize="small" />, color: 'primary.main', iconColor: 'primary.light', bgColor: 'primary.50' },
  IMPORTANT: { icon: <PriorityHighIcon fontSize="small" />, color: 'error.main', iconColor: 'error.light', bgColor: 'error.50' },
  QUESTION: { icon: <HelpOutlineIcon fontSize="small" />, color: 'warning.main', iconColor: 'warning.light', bgColor: 'warning.50' },
  EXAMPLE: { icon: <LightbulbOutlinedIcon fontSize="small" />, color: 'success.main', iconColor: 'success.light', bgColor: 'success.50' },
  INFO: { icon: <InfoOutlinedIcon fontSize="small" />, color: 'grey.500', iconColor: 'grey.500', bgColor: 'grey.50' },
  SUMMARY: { icon: <SummarizeOutlinedIcon fontSize="small" />, color: 'primary.main', iconColor: 'primary.main', bgColor: 'primary.50' },
};

/** Walk React children to find [!TYPE] pattern in the first text node of a blockquote. */
function extractCalloutType(children: ReactNode): { type: string; content: ReactNode } | null {
  const childArray = Children.toArray(children);

  for (let i = 0; i < childArray.length; i++) {
    const child = childArray[i];
    if (!isValidElement(child)) continue;

    const typedChild = child as ReactElement<{ children?: ReactNode }>;
    if (!typedChild.props?.children) continue;

    const pChildren = Children.toArray(typedChild.props.children);
    if (pChildren.length === 0) continue;

    const firstChild = pChildren[0];
    if (typeof firstChild !== 'string') continue;

    const match = firstChild.match(/^\[!(\w+)\]\s*/);
    if (!match || !CALLOUT_CONFIG[match[1]]) continue;

    const type = match[1];
    const remainingText = firstChild.slice(match[0].length);
    const newPChildren = [
      ...(remainingText ? [remainingText] : []),
      ...pChildren.slice(1),
    ];
    const newChild = cloneElement(typedChild, {}, ...newPChildren);
    const newChildren = [...childArray.slice(0, i), newChild, ...childArray.slice(i + 1)];
    return { type, content: newChildren };
  }

  return null;
}

function MarkdownBlockquote({ children }: { children?: ReactNode }) {
  const callout = extractCalloutType(children);

  if (callout) {
    const config = CALLOUT_CONFIG[callout.type];
    const isQuote = callout.type === 'QUOTE';

    if (isQuote) {
      return (
        <Box
          sx={{
            borderLeft: 4,
            borderColor: config.color,
            bgcolor: config.bgColor,
            borderRadius: 1,
            px: 2.5,
            py: 2,
            my: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography component="span" sx={{ color: config.iconColor, fontSize: '2.5rem', fontFamily: 'Georgia, serif', lineHeight: 0.8, flexShrink: 0, opacity: 0.5, mt: 0.5 }}>
              &ldquo;
            </Typography>
            <Box sx={{ '& p': { mb: 0, fontStyle: 'italic', color: 'text.secondary' } }}>
              {callout.content}
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          borderLeft: 4,
          borderColor: config.color,
          bgcolor: config.bgColor,
          borderRadius: 1,
          px: 2.5,
          py: 2,
          my: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ color: config.iconColor, display: 'flex', opacity: 0.7, flexShrink: 0, mt: 0.3 }}>{config.icon}</Box>
          <Box sx={{ '& p': { mb: 0, '&:last-of-type': { mb: 0 } } }}>
            {callout.content}
          </Box>
        </Box>
      </Box>
    );
  }

  // Default blockquote — light primary tint
  return (
    <Box
      component="blockquote"
      sx={{
        borderLeft: 4,
        borderColor: 'primary.light',
        bgcolor: 'primary.50',
        pl: 2.5,
        py: 1.5,
        pr: 2,
        my: 3,
        mx: 0,
        borderRadius: 1,
        '& p': { fontStyle: 'italic', color: 'text.secondary', mb: 0 },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Shared sx styles for ReactMarkdown body containers across all resource detail pages.
 */
export const markdownBodyStyles: SxProps<Theme> = {
  '& h2': {
    mt: 4,
    mb: 2,
    fontSize: '1.75rem',
    fontWeight: 600,
  },
  '& h3': {
    mt: 3,
    mb: 1.5,
    fontSize: '1.375rem',
    fontWeight: 600,
  },
  '& p': {
    mb: 2,
    lineHeight: 1.8,
    color: 'text.secondary',
  },
  '& ul, & ol': {
    mb: 2,
    pl: 3,
  },
  '& li': {
    mb: 1,
    color: 'text.secondary',
  },
  '& strong': {
    fontWeight: 600,
    color: 'text.primary',
  },
  '& a': {
    color: 'primary.main',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& hr': {
    border: 'none',
    borderTop: '1px solid',
    borderColor: 'divider',
    my: 4,
  },
  '& .image-row': {
    display: 'flex',
    gap: 3,
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    my: 3,
    '& > p': {
      display: 'contents',
      m: 0,
    },
    '& img, & figure': {
      flex: '0 1 auto',
      // Override inline sx styles from the image component
      marginLeft: '0 !important',
      marginRight: '0 !important',
      marginTop: '0 !important',
      marginBottom: '0 !important',
    },
    // Figures always use flex column so their inner image + caption are centered
    '& figure': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    '& figure img': {
      maxWidth: '100%',
    },
    // Only constrain unsized images to 48%; explicitly sized images keep their |width value
    '& img:not([data-explicit-width]), & figure:not([data-explicit-width])': {
      maxWidth: { xs: '100%', sm: '48%' },
    },
    // When --row-height is set on the container, images match that height and
    // keep their own aspect ratio. The 48% cap above would otherwise apply to
    // the figure and letterbox a wide image inside a too-narrow box — so a
    // landscape screenshot rendered visibly shorter than a portrait one beside
    // it, which is the opposite of what --row-height is for.
    // On xs the row stacks, so the fixed height is dropped entirely and images
    // go full-width — otherwise a wide screenshot keeps its --row-height-derived
    // width and blows past the viewport.
    '&[style*="--row-height"] figure': {
      maxWidth: { xs: '100%', sm: 'none' },
    },
    '&[style*="--row-height"] img, &[style*="--row-height"] figure img': {
      height: { xs: 'auto', sm: 'var(--row-height)' },
      width: { xs: '100%', sm: 'auto' },
      maxWidth: { xs: '100%', sm: 'none' },
      objectFit: 'contain',
    },
  },
  // --- Table styles ---
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    my: 3,
    fontSize: '0.9rem',
  },
  '& th': {
    textAlign: 'left',
    fontWeight: 600,
    color: 'text.primary',
    borderBottom: '2px solid',
    borderColor: 'divider',
    px: 1.5,
    py: 1,
  },
  '& td': {
    color: 'text.secondary',
    borderBottom: '1px solid',
    borderColor: 'divider',
    px: 1.5,
    py: 1,
  },
  '& tr:last-child td': {
    borderBottom: 'none',
  },
  // --- Importance color classes (use with <span class="importance-high">) ---
  '& .importance-high': {
    color: '#c62828',
    fontWeight: 600,
  },
  '& .importance-medium': {
    color: '#e65100',
    fontWeight: 600,
  },
  '& .importance-low': {
    color: '#2e7d32',
    fontWeight: 600,
  },
  '& .content-row': {
    display: 'flex',
    gap: 3,
    alignItems: 'center',
    flexWrap: 'wrap',
    my: 3,
    '& > *': {
      flex: '1 1 0',
      maxWidth: { xs: '100%', sm: '48%' },
      my: 0,
    },
    '& img, & figure': {
      display: 'block',
    },
    '& figure img': {
      maxWidth: '100%',
    },
  },
};

/**
 * Shared image component factory for ReactMarkdown's `components` prop.
 * Pass the content-type-specific image URL resolver.
 */
/** Parse Obsidian-style width from alt text: "alt text|300" → { cleanAlt: "alt text", width: 300 } */
function parseImageSize(alt: string | undefined): { cleanAlt: string; width?: number } {
  if (!alt) return { cleanAlt: '' };
  const match = alt.match(/^(.*?)\|(\d+)$/);
  if (match) return { cleanAlt: match[1].trim(), width: Number(match[2]) };
  return { cleanAlt: alt };
}

export function createMarkdownImageComponent(resolveImageUrl: (src: string | undefined) => string | undefined) {
  return function MarkdownImage({ src, alt, title, ...props }: { src?: string; alt?: string; title?: string } & Record<string, any>) {
    const { cleanAlt, width } = parseImageSize(alt);
    const sizeConstraint = width ? { maxWidth: { xs: '100%', md: width } } : { maxWidth: { xs: '100%', md: '70%' } };

    const explicitWidthProps = width ? { 'data-explicit-width': true } : {};

    const imgElement = (
      <Box
        component="img"
        src={resolveImageUrl(src) || src}
        alt={cleanAlt}
        {...explicitWidthProps}
        {...props}
        sx={{
          ...sizeConstraint,
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          borderRadius: 2,
          boxShadow: marketing.shadows.hero,
          my: 3,
          display: 'block',
          mx: 'auto',
          ...props.sx,
        }}
      />
    );

    if (title) {
      return (
        <Box
          component="figure"
          {...explicitWidthProps}
          sx={{ my: 3, mx: 'auto', textAlign: 'center', ...sizeConstraint }}
        >
          <Box
            component="img"
            src={resolveImageUrl(src) || src}
            alt={cleanAlt}
            {...props}
            sx={{
              maxWidth: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: marketing.shadows.hero,
              display: 'block',
              mx: 'auto',
              ...props.sx,
            }}
          />
          <Typography
            component="figcaption"
            sx={{
              fontStyle: 'italic',
              color: 'text.secondary',
              fontSize: '0.875rem',
              mt: 1,
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>
        </Box>
      );
    }

    return imgElement;
  };
}

/**
 * Slugify heading text into a URL-safe anchor id, so in-article links like
 * [jump to the audit](#the-5-minute-notification-audit) resolve. Matches
 * GitHub-style slugs: lowercase, punctuation stripped, spaces → hyphens.
 */
export function slugifyHeading(children: ReactNode): string {
  const text = Children.toArray(children)
    .map((child) => (typeof child === 'string' || typeof child === 'number' ? String(child) : ''))
    .join('');
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

function createHeadingComponent(Tag: 'h2' | 'h3') {
  return function MarkdownHeading({ children }: { children?: ReactNode }) {
    return <Tag id={slugifyHeading(children)}>{children}</Tag>;
  };
}

/**
 * Creates the full set of ReactMarkdown component overrides.
 * Pass the content-type-specific image URL resolver.
 */
export function createMarkdownComponents(resolveImageUrl: (src: string | undefined) => string | undefined) {
  return {
    img: createMarkdownImageComponent(resolveImageUrl),
    blockquote: MarkdownBlockquote,
    h2: createHeadingComponent('h2'),
    h3: createHeadingComponent('h3'),
  };
}
